/**
 * A non-throwing tokenizer for the formula language, mirroring the character classes `tokenize()`
 * in `src/shared/formula/language.ts` accepts (numbers with the locale decimal separator,
 * `[Column]`/`[@Column]`/`[Column]12`/`[Column]@'id'` references, dotted `'Doc'.'Sheet'` container
 * paths (quotes optional), `"text"`
 * literals, operators, parens, the locale argument separator, and function calls) — but where the
 * real tokenizer throws on the first invalid character, THIS one always makes forward progress
 * and tags the offending span `invalid` instead. That difference is deliberate: a live editor is
 * showing an in-progress formula on every keystroke, and `parseFormula` on partial input is
 * `ok:false` far more often than not (see that module's own doc comment) — a highlighter that
 * stops dead at the first bad character would render nothing past `=SU`.
 *
 * `tokenizeLenient` is a plain `(source, locale) → token[]` function, shared by the CM6
 * `StreamLanguage` below (tokenizing one line at a time) and by `formulaSignature.ts` (tokenizing
 * the whole formula, since `\n` is whitespace in this grammar — the same function works for both,
 * no duplicated lexing logic).
 *
 * Function names are looked up in `FUNCTIONS` from `language.ts` — the single source of truth,
 * never a duplicated list.
 */

import { StreamLanguage, LanguageSupport, type StreamParser } from '@codemirror/language'
import { FUNCTIONS, NAME_PART, NAME_START, type FormulaLocale } from '@/shared/formula/language'
import { isGlobalFormatName } from '@/shared/formula/globalFormats'

export type LenientTokenKind =
  | 'number'
  | 'string'
  | 'containerName'
  | 'dot'
  | 'ref'
  | 'func'
  | 'identifier'
  | 'operator'
  | 'percent'
  | 'colon'
  | 'open'
  | 'close'
  | 'argSep'
  | 'invalid'

export interface LenientToken {
  kind: LenientTokenKind
  start: number
  end: number
  text: string
  /** `ref`: the bracket contents. `containerName`: the name with `''` unescaped.
   *  `func`/`identifier`: the upper-cased name. */
  name?: string
  /** `containerName` only: whether the source wrapped it in apostrophes. */
  quoted?: boolean
}

const isDigit = (c: string): boolean => c >= '0' && c <= '9'

/** The character at `i` as a whole code point — see the twin helper in `language.ts`. Emoji are
 *  two UTF-16 units, and reading one gives an unpaired surrogate that highlights as `invalid`. */
const codePointAt = (source: string, i: number): string => {
  const point = source.codePointAt(i)
  return point === undefined ? '' : String.fromCodePoint(point)
}

export function tokenizeLenient(source: string, locale: FormulaLocale): LenientToken[] {
  const tokens: LenientToken[] = []
  let i = 0

  const push = (kind: LenientTokenKind, start: number, end: number, name?: string, quoted?: boolean): void => {
    tokens.push({
      kind, start, end, text: source.slice(start, end),
      ...(name === undefined ? {} : { name }),
      ...(quoted === undefined ? {} : { quoted }),
    })
  }

  while (i < source.length) {
    // A whole code point, so an emoji is one character rather than two broken halves.
    const c = codePointAt(source, i)
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++
      continue
    }
    const start = i

    // Same previous-token rule as the strict lexer, so highlighting agrees with parsing.
    if (c === '.' && tokens[tokens.length - 1]?.kind === 'containerName') {
      i++
      push('dot', start, i)
      continue
    }

    if (isDigit(c) || c === locale.decimal) {
      while (i < source.length && isDigit(source.charAt(i))) i++
      if (source.charAt(i) === locale.decimal) {
        i++
        while (i < source.length && isDigit(source.charAt(i))) i++
      }
      if (!/\d/.test(source.slice(start, i))) {
        push('invalid', start, i)
        continue
      }
      push('number', start, i)
      continue
    }

    if (c === '[') {
      i++
      if (source.charAt(i) === '@') i++
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === ']') {
          if (source.charAt(i + 1) === ']') {
            i += 2
            continue
          }
          i++
          closed = true
          break
        }
        i++
      }
      if (!closed) {
        push('invalid', start, i)
        continue
      }
      while (i < source.length && isDigit(source.charAt(i))) i++
      if (source.slice(i, i + 2) === "@'") {
        i += 2
        let idClosed = false
        while (i < source.length) {
          if (source.charAt(i) === "'") {
            if (source.charAt(i + 1) === "'") {
              i += 2
              continue
            }
            i++
            idClosed = true
            break
          }
          i++
        }
        if (!idClosed) {
          push('invalid', start, i)
          continue
        }
      }
      push('ref', start, i, source.slice(start, i))
      continue
    }

    if (c === "'") {
      i++
      let name = ''
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === "'") {
          if (source.charAt(i + 1) === "'") {
            name += "'"
            i += 2
            continue
          }
          i++
          closed = true
          break
        }
        name += source.charAt(i)
        i++
      }
      // Still being typed: highlight it as a literal rather than screaming, the same way the
      // rest of this tokenizer treats an unfinished token.
      if (!closed) {
        push('string', start, i)
        continue
      }
      push('containerName', start, i, name, true)
      continue
    }

    if (c === '"') {
      i++
      while (i < source.length) {
        if (source.charAt(i) === '"') {
          if (source.charAt(i + 1) === '"') {
            i += 2
            continue
          }
          i++
          break
        }
        i++
      }
      push('string', start, i)
      continue
    }

    if (c === '!') {
      i++
      push('invalid', start, i)
      continue
    }

    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^' || c === '&' || c === '=' || c === '<' || c === '>') {
      i++
      push('operator', start, i)
      continue
    }
    if (c === '%') {
      i++
      push('percent', start, i)
      continue
    }
    if (c === ':') {
      i++
      push('colon', start, i)
      continue
    }
    if (c === '(') {
      i++
      push('open', start, i)
      continue
    }
    if (c === ')') {
      i++
      push('close', start, i)
      continue
    }
    if (c === locale.argument) {
      i++
      push('argSep', start, i)
      continue
    }

    if (NAME_START.test(c)) {
      while (i < source.length) {
        const next = codePointAt(source, i)
        if (!NAME_PART.test(next)) break
        i += next.length
      }
      const word = source.slice(start, i)
      const name = word.toLocaleUpperCase('es')
      // A function only when it is actually called; `SUM` alone is a name the user may still be
      // typing, and every other bare word is an unquoted container name.
      if (source.charAt(i) === '(') {
        // Called: it is a function or a mistake, never a container name.
        push(name in FUNCTIONS ? 'func' : 'invalid', start, i, name)
      } else if (name in FUNCTIONS || isGlobalFormatName(word)) {
        push('identifier', start, i, name in FUNCTIONS ? name : word)
      } else {
        push('containerName', start, i, word, false)
      }
      continue
    }

    i += c.length
    push('invalid', start, i)
  }

  return tokens
}

const TAG_OF: Record<LenientTokenKind, string> = {
  number: 'number',
  string: 'string',
  containerName: 'string',
  dot: 'punctuation',
  ref: 'variableName',
  func: 'variableName.function',
  identifier: 'propertyName',
  operator: 'operator',
  percent: 'operator',
  colon: 'punctuation',
  open: 'paren',
  close: 'paren',
  argSep: 'separator',
  invalid: 'invalid',
}

interface FormulaStreamState {
  lineText: string
  tokens: LenientToken[]
  index: number
}

function formulaStreamParser(locale: FormulaLocale): StreamParser<FormulaStreamState> {
  return {
    name: 'formula',
    startState: () => ({ lineText: '', tokens: [], index: 0 }),
    token(stream, state) {
      if (stream.sol() || state.lineText !== stream.string) {
        state.lineText = stream.string
        state.tokens = tokenizeLenient(stream.string, locale)
        state.index = 0
      }
      const tok = state.tokens[state.index]
      if (!tok) {
        stream.skipToEnd()
        return null
      }
      if (stream.pos < tok.start) {
        // Whitespace the tokenizer skipped without emitting a token for.
        stream.pos = tok.start
        return null
      }
      stream.pos = tok.end
      state.index++
      return TAG_OF[tok.kind]
    },
  }
}

export function formulaLanguage(locale: FormulaLocale): LanguageSupport {
  return new LanguageSupport(StreamLanguage.define(formulaStreamParser(locale)))
}
